/**
 * Web-specific Leaflet Map Component
 * Uses react-leaflet for web PWA support
 */

import React, { useEffect, useRef, useState } from 'react';
import { View, StyleSheet } from 'react-native';
import { MapContainer, TileLayer, Marker, Popup, ImageOverlay, Polyline, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icons in react-leaflet - only run on client
if (typeof window !== 'undefined') {
  delete (L.Icon.Default.prototype as any)._getIconUrl;
  L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
    iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  });
}

interface LeafletMapWebProps {
  center: { lat: number; lng: number };
  zoom: number;
  markers: Array<{
    position: { lat: number; lng: number };
    icon: string;
    size: [number, number];
    title: string;
    temperature?: number;
    chlorophyll?: number;
    currentSpeed?: number;
    currentDirection?: number;
    ssh?: number;
    type?: string;
    noCluster?: boolean;
    hotspotNumber?: number;
    confidence?: number;
    score?: number;
    reasons?: string[];
    gradients?: string[];
    nearbyStructures?: any[];
    buoyData?: any;
    description?: string;
    category?: string;
  }>;
  overlays?: Array<{
    url: string;
    bounds: {
      minLat: number;
      maxLat: number;
      minLon: number;
      maxLon: number;
    };
    zIndex?: number;
    type?: 'image' | 'tile' | 'custom-bag' | 'custom-multibeam';
    opacity?: number;
  }>;
  shapes?: Array<{
    shapeType: 'polyline';
    positions: Array<{ lat: number; lng: number }>;
    color?: string;
    weight?: number;
    distance?: number;
  }>;
  onMapClick?: (lat: number, lng: number) => void;
  onMapLongPress?: (lat: number, lng: number) => void;
  onMarkerClick?: (position: { lat: number; lng: number }) => void;
  onMapMove?: (center: { lat: number; lng: number }, zoom: number, bounds: { minLat: number; maxLat: number; minLon: number; maxLon: number }) => void;
  enableClustering?: boolean;
  baseMapType?: 'street' | 'ocean';
}

// Component to handle map events
function MapEventHandler({ onMapClick, onMapMove, onMapLongPress }: any) {
  const map = useMapEvents({
    click: (e) => {
      if (onMapClick) {
        onMapClick(e.latlng.lat, e.latlng.lng);
      }
    },
    contextmenu: (e) => {
      if (onMapLongPress) {
        onMapLongPress(e.latlng.lat, e.latlng.lng);
      }
    },
    moveend: () => {
      if (onMapMove) {
        const center = map.getCenter();
        const bounds = map.getBounds();
        onMapMove(
          { lat: center.lat, lng: center.lng },
          map.getZoom(),
          {
            minLat: bounds.getSouth(),
            maxLat: bounds.getNorth(),
            minLon: bounds.getWest(),
            maxLon: bounds.getEast()
          }
        );
      }
    },
    zoomend: () => {
      if (onMapMove) {
        const center = map.getCenter();
        const bounds = map.getBounds();
        onMapMove(
          { lat: center.lat, lng: center.lng },
          map.getZoom(),
          {
            minLat: bounds.getSouth(),
            maxLat: bounds.getNorth(),
            minLon: bounds.getWest(),
            maxLon: bounds.getEast()
          }
        );
      }
    }
  });
  return null;
}

// Component to update map view
function MapViewController({ center, zoom }: { center: { lat: number; lng: number }, zoom: number }) {
  const map = useMap();

  useEffect(() => {
    map.setView([center.lat, center.lng], zoom);
  }, [center.lat, center.lng, zoom, map]);

  return null;
}

// Component to create custom panes for layer ordering
function PaneCreator() {
  const map = useMap();

  useEffect(() => {
    // Create a custom pane for bathymetry overlays - should be below ocean data
    if (!map.getPane('bathymetry')) {
      const pane = map.createPane('bathymetry');
      pane.style.zIndex = '350'; // Between tilePane (200) and overlayPane (400) - renders below ocean data
    }

    // Weather radar must sit above the ocean basemap and reference labels,
    // while remaining below markers and popups.
    if (!map.getPane('radar')) {
      const pane = map.createPane('radar');
      pane.style.zIndex = '450';
      pane.style.pointerEvents = 'none';
    }
  }, [map]);

  return null;
}

// Custom bathymetry tile layer component that uses Leaflet's API directly
function BathymetryLayer({ serviceUrl, opacity, type }: { serviceUrl: string; opacity: number; type: string }) {
  const map = useMap();
  const layerRef = useRef<L.TileLayer | null>(null);

  useEffect(() => {
    // Create custom tile layer with getTileUrl function
    const CustomTileLayer = L.TileLayer.extend({
      getTileUrl: function (coords: any) {
        // Calculate bbox from tile coordinates
        const tileBounds = this._tileCoordsToBounds(coords);
        const sw = tileBounds.getSouthWest();
        const ne = tileBounds.getNorthEast();

        // Convert to Web Mercator (EPSG:3857)
        const swProj = L.CRS.EPSG3857.project(sw);
        const neProj = L.CRS.EPSG3857.project(ne);

        const bbox = `${swProj.x},${swProj.y},${neProj.x},${neProj.y}`;

        // Build the URL with bbox parameter
        return `${serviceUrl}?bbox=${bbox}&size=256,256&format=png32&f=image&bboxSR=3857&imageSR=3857&transparent=true`;
      }
    });

    // Create and add the layer
    const layer = new (CustomTileLayer as any)('', {
      opacity: opacity,
      maxZoom: 20,
      maxNativeZoom: 13,
      pane: 'bathymetry',
      attribution: ''
    });

    layer.addTo(map);
    layerRef.current = layer;

    console.log('Custom bathymetry layer added:', serviceUrl, 'type:', type);

    // Cleanup
    return () => {
      if (layerRef.current) {
        map.removeLayer(layerRef.current);
        layerRef.current = null;
      }
    };
  }, [map, serviceUrl, opacity, type]);

  return null;
}

export const LeafletMapWeb: React.FC<LeafletMapWebProps> = ({
  center,
  zoom,
  markers = [],
  overlays = [],
  shapes = [],
  onMapClick,
  onMapLongPress,
  onMarkerClick,
  onMapMove,
  enableClustering = false,
  baseMapType = 'street'
}) => {
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    // Only render map on client side
    setIsClient(true);
  }, []);

  const safeCenter = center && center.lat !== undefined && center.lng !== undefined
    ? center
    : { lat: 25.0, lng: -90.0 };

  const safeZoom = zoom !== undefined && zoom !== null ? zoom : 8;

  // Don't render until we're on the client
  if (!isClient) {
    return (
      <View style={styles.container}>
        <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
          {/* Loading placeholder */}
        </View>
      </View>
    );
  }

  // Create custom icon for each marker
  const createDivIcon = (icon: string, size: [number, number]) => {
    return L.divIcon({
      html: `<div style="font-size: ${size[0] - 8}px; width: ${size[0]}px; height: ${size[1]}px; display: flex; align-items: center; justify-content: center;">${icon}</div>`,
      iconSize: size,
      className: 'emoji-marker',
      iconAnchor: [size[0] / 2, size[1] / 2]
    });
  };

  return (
    <View style={styles.container}>
      <MapContainer
        center={[safeCenter.lat, safeCenter.lng]}
        zoom={safeZoom}
        style={{ height: '100%', width: '100%' }}
        zoomControl={false}
        attributionControl={false}
      >
        {/* Base map tiles */}
        {baseMapType === 'ocean' ? (
          <>
            <TileLayer
              url="https://server.arcgisonline.com/ArcGIS/rest/services/Ocean/World_Ocean_Base/MapServer/tile/{z}/{y}/{x}"
              attribution=""
              maxZoom={20}
              maxNativeZoom={13}
            />
            <TileLayer
              url="https://server.arcgisonline.com/ArcGIS/rest/services/Ocean/World_Ocean_Reference/MapServer/tile/{z}/{y}/{x}"
              attribution=""
              maxZoom={20}
              maxNativeZoom={13}
              opacity={1.0}
              zIndex={1000}
            />
          </>
        ) : (
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution=""
          />
        )}

        {/* Bathymetry tile layers - NOAA relief shading (same as mobile app) */}
        {overlays.filter(o => o.type === 'custom-bag' || o.type === 'custom-multibeam').map((overlay, index) => {
          // Use NOAA NCEI bathymetry ImageServer - exact same service as mobile app
          const serviceUrl = overlay.type === 'custom-multibeam'
            ? 'https://gis.ngdc.noaa.gov/arcgis/rest/services/multibeam_mosaic/ImageServer/exportImage'
            : 'https://gis.ngdc.noaa.gov/arcgis/rest/services/bag_bathymetry/ImageServer/exportImage';

          const overlayOpacity = overlay.opacity !== undefined ? overlay.opacity : 0.7;

          return (
            <BathymetryLayer
              key={`bathymetry-${index}`}
              serviceUrl={serviceUrl}
              opacity={overlayOpacity}
              type={overlay.type || 'custom-bag'}
            />
          );
        })}

        {/* Image overlays */}
        {overlays.filter(o => o.type === 'image' || !o.type).map((overlay, index) => (
          <ImageOverlay
            key={`overlay-${index}`}
            url={overlay.url}
            bounds={[
              [overlay.bounds.minLat, overlay.bounds.minLon],
              [overlay.bounds.maxLat, overlay.bounds.maxLon]
            ]}
            opacity={overlay.opacity !== undefined ? overlay.opacity : 1.0}
            zIndex={overlay.zIndex || index}
          />
        ))}

        {/* Weather radar is supplied as an XYZ tile URL by RainViewer. */}
        {overlays.filter(o => o.type === 'tile').map((overlay, index) => (
          <TileLayer
            key={`tile-overlay-${index}-${overlay.url}`}
            url={overlay.url}
            pane="radar"
            opacity={overlay.opacity !== undefined ? overlay.opacity : 0.7}
            maxNativeZoom={7}
            maxZoom={20}
            eventHandlers={{
              tileerror: (event) => console.error('Radar tile failed to load:', event.tile?.src),
            }}
          />
        ))}

        {/* Markers */}
        {markers.map((marker, index) => (
          <Marker
            key={`marker-${index}`}
            position={[marker.position.lat, marker.position.lng]}
            icon={createDivIcon(marker.icon, marker.size)}
            eventHandlers={{
              click: () => {
                if (onMarkerClick) {
                  onMarkerClick(marker.position);
                }
              }
            }}
          >
            <Popup>
              <div style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
                <strong>{marker.title}</strong>

                {/* Buoy-specific data */}
                {marker.type === 'buoy' && marker.buoyData && (
                  <div>
                    {marker.buoyData.station_id && (
                      <div style={{ fontSize: '11px', color: '#666', marginBottom: '4px' }}>
                        Station: {marker.buoyData.station_id}
                      </div>
                    )}
                    {marker.buoyData.observation_time && (
                      <div style={{ fontSize: '11px', color: '#666', marginBottom: '8px' }}>
                        {new Date(marker.buoyData.observation_time).toLocaleString()}
                      </div>
                    )}

                    {/* Wind data */}
                    {(marker.buoyData.wind_speed !== undefined || marker.buoyData.wind_direction !== undefined) && (
                      <div style={{ marginTop: '4px' }}>
                        <strong style={{ fontSize: '12px' }}>Wind:</strong>
                        {marker.buoyData.wind_speed !== undefined && (
                          <div>💨 {marker.buoyData.wind_speed.toFixed(1)} kts</div>
                        )}
                        {marker.buoyData.wind_direction !== undefined && (
                          <div>🧭 {marker.buoyData.wind_direction.toFixed(0)}°</div>
                        )}
                        {marker.buoyData.wind_gust !== undefined && (
                          <div>💨 Gusts: {marker.buoyData.wind_gust.toFixed(1)} kts</div>
                        )}
                      </div>
                    )}

                    {/* Wave data */}
                    {(marker.buoyData.wave_height !== undefined || marker.buoyData.dominant_wave_period !== undefined) && (
                      <div style={{ marginTop: '4px' }}>
                        <strong style={{ fontSize: '12px' }}>Waves:</strong>
                        {marker.buoyData.wave_height !== undefined && (
                          <div>🌊 {marker.buoyData.wave_height.toFixed(1)} ft</div>
                        )}
                        {marker.buoyData.dominant_wave_period !== undefined && (
                          <div>⏱️ {marker.buoyData.dominant_wave_period.toFixed(1)} sec</div>
                        )}
                        {marker.buoyData.wave_direction !== undefined && (
                          <div>🧭 {marker.buoyData.wave_direction.toFixed(0)}°</div>
                        )}
                      </div>
                    )}

                    {/* Temperature data */}
                    {(marker.buoyData.water_temperature !== undefined || marker.buoyData.air_temperature !== undefined) && (
                      <div style={{ marginTop: '4px' }}>
                        <strong style={{ fontSize: '12px' }}>Temperature:</strong>
                        {marker.buoyData.water_temperature !== undefined && (
                          <div>🌡️ Water: {marker.buoyData.water_temperature.toFixed(1)}°F</div>
                        )}
                        {marker.buoyData.air_temperature !== undefined && (
                          <div>🌡️ Air: {marker.buoyData.air_temperature.toFixed(1)}°F</div>
                        )}
                      </div>
                    )}

                    {/* Pressure */}
                    {marker.buoyData.sea_level_pressure !== undefined && (
                      <div style={{ marginTop: '4px' }}>
                        <strong style={{ fontSize: '12px' }}>Pressure:</strong>
                        <div>🔘 {marker.buoyData.sea_level_pressure.toFixed(1)} mb</div>
                      </div>
                    )}

                    {/* Visibility */}
                    {marker.buoyData.visibility !== undefined && (
                      <div style={{ marginTop: '4px' }}>
                        <strong style={{ fontSize: '12px' }}>Visibility:</strong>
                        <div>👁️ {marker.buoyData.visibility.toFixed(1)} nm</div>
                      </div>
                    )}
                  </div>
                )}

                {/* Hotspot data */}
                {marker.type === 'hotspot' && (
                  <div>
                    {marker.hotspotNumber !== undefined && (
                      <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#2196F3', marginBottom: '4px' }}>
                        Hotspot #{marker.hotspotNumber}
                      </div>
                    )}
                    {marker.score !== undefined && (
                      <div>⭐ Score: {marker.score.toFixed(1)}</div>
                    )}
                    {marker.reasons && marker.reasons.length > 0 && (
                      <div style={{ marginTop: '4px', fontSize: '12px' }}>
                        <strong>Reasons:</strong>
                        {marker.reasons.map((reason, idx) => (
                          <div key={idx}>• {reason}</div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Generic oceanographic data */}
                {marker.type !== 'buoy' && marker.type !== 'hotspot' && (
                  <div>
                    {marker.temperature !== undefined && (
                      <div>🌡️ Temp: {((marker.temperature * 9/5) + 32).toFixed(1)}°F</div>
                    )}
                    {marker.chlorophyll !== undefined && (
                      <div>🌿 Chlorophyll: {marker.chlorophyll.toFixed(3)} mg/m³</div>
                    )}
                    {marker.currentSpeed !== undefined && (
                      <div>💨 Current: {marker.currentSpeed.toFixed(1)} kts</div>
                    )}
                  </div>
                )}

                {/* Waypoint description */}
                {marker.description && (
                  <div style={{ marginTop: '8px', fontSize: '12px', color: '#555' }}>
                    {marker.description}
                  </div>
                )}

                {/* Category */}
                {marker.category && (
                  <div style={{ marginTop: '4px', fontSize: '11px', color: '#666' }}>
                    Category: {marker.category}
                  </div>
                )}

                <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
                  📍 {marker.position.lat.toFixed(6)}°, {marker.position.lng.toFixed(6)}°
                </div>
              </div>
            </Popup>
          </Marker>
        ))}

        {/* Polylines */}
        {shapes.filter(s => s.shapeType === 'polyline').map((shape, index) => {
          // Calculate distance and midpoint for 2-point polylines
          let distanceLabel = null;
          if (shape.positions.length === 2) {
            const lat1 = shape.positions[0].lat;
            const lng1 = shape.positions[0].lng;
            const lat2 = shape.positions[1].lat;
            const lng2 = shape.positions[1].lng;

            // Haversine formula for distance in miles
            const R = 3959; // Earth's radius in miles
            const dLat = (lat2 - lat1) * Math.PI / 180;
            const dLng = (lng2 - lng1) * Math.PI / 180;
            const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
                      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
                      Math.sin(dLng/2) * Math.sin(dLng/2);
            const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
            const distance = R * c;

            // Calculate midpoint
            const midLat = (lat1 + lat2) / 2;
            const midLng = (lng1 + lng2) / 2;

            // Create custom icon for distance label
            const distanceIcon = L.divIcon({
              className: 'distance-label',
              html: `<div style="background: white; padding: 6px 10px; border-radius: 4px; border: 2px solid #000000; font-weight: bold; font-size: 14px; color: #000000; white-space: nowrap; box-shadow: 0 2px 4px rgba(0,0,0,0.2);">${distance.toFixed(2)} mi</div>`,
              iconSize: [100, 30],
              iconAnchor: [50, 15]
            });

            distanceLabel = (
              <Marker
                key={`distance-${index}`}
                position={[midLat, midLng]}
                icon={distanceIcon}
              />
            );
          }

          return (
            <React.Fragment key={`polyline-group-${index}`}>
              <Polyline
                key={`polyline-${index}`}
                positions={shape.positions.map(p => [p.lat, p.lng] as [number, number])}
                color={shape.color || '#FF0000'}
                weight={shape.weight || 3}
                opacity={1.0}
              />
              {distanceLabel}
            </React.Fragment>
          );
        })}

        {/* Custom panes for layer ordering */}
        <PaneCreator />

        {/* Map event handlers */}
        <MapEventHandler
          onMapClick={onMapClick}
          onMapMove={onMapMove}
          onMapLongPress={onMapLongPress}
        />

        {/* View controller */}
        <MapViewController center={safeCenter} zoom={safeZoom} />
      </MapContainer>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    height: '100%',
    width: '100%',
  },
});
